import React, { useState, useContext, useEffect } from 'react'
import styles from './Lpdp.module.css'
import Subtitle from '../../components/Subtitle/Subtitle'
import Footer from '../../components/Footer'
import Header from '../../components/Header'

import SubMenu from '../../components/SubMenu'
import GeneralContext from '../../context/GeneralContext'
import * as GeneralActions from '../../store/actions/generalActions'

//Services
import { obtenerEstadoSolicitudMultiple } from "../../services/repositories/solicitud-multiple-repository";
import { actualizarSolicitud } from "../../services/repositories/solicitud-repository";
import {
    actualizarConsentimiento,
    consultarConsentimiento,
    obtenerDocumentosConsentimiento,
    registrarConsentimiento
} from "../../services/repositories/consentimiento-repository";

const CONSENT_STATUS_APPROVED = 'Aprobado'
const CONSENT_STATUS_REJECTED = 'No Aprobado'
const CONSENT_STATUS_REJECTED_COMPAT = 'Rechazado'
const CONSENT_STATUS_REVOKED = 'Revocado'
const CONSENT_STATUS_CANCELLED = 'Cancelado'
const CONSENT_STATUS_OPPOSITION = 'Oposicion'
const CONSENT_STATUS_REVOCATION = 'Revocacion'
const CONSENT_ALREADY_APPROVED_TEXT = 'Consentimiento previamente aprobado.'
const CONSENT_ALREADY_REGISTERED_TEXT = 'Consentimiento previamente registrado. No requiere nueva confirmacion.'
const CONSENT_REVOKED_INFO_TEXT = 'El consentimiento se encuentra revocado. Para esta solicitud solo se muestra informacion y no se puede modificar desde este flujo.'
const LEGACY_CONSENT_DEFAULT_FLAG = 2

const CONSENT_DISPLAY_MODE_INTERACTIVE = 'interactive'
const CONSENT_DISPLAY_MODE_APPROVED = 'approved'
const CONSENT_DISPLAY_MODE_REGISTERED = 'registered'
const CONSENT_DISPLAY_MODE_REVOKED = 'revoked'

const normalizeConsentStatus = (status) => {
    if (status === null || status === undefined) return null

    const normalizedStatus = String(status).trim()
    if (normalizedStatus === '') return null

    return normalizedStatus
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
}

const isRevokedConsentStatus = (status) => {
    const normalizedStatus = normalizeConsentStatus(status)
    if (!normalizedStatus) return false

    return (
        normalizedStatus === CONSENT_STATUS_REVOKED.toUpperCase() ||
        normalizedStatus === CONSENT_STATUS_CANCELLED.toUpperCase() ||
        normalizedStatus === CONSENT_STATUS_OPPOSITION.toUpperCase() ||
        normalizedStatus === CONSENT_STATUS_REVOCATION.toUpperCase()
    )
}

const mapBooleanToConsentStatus = (value) => {
    return value ? CONSENT_STATUS_APPROVED : CONSENT_STATUS_REJECTED
}

const mapConsentStatusToBoolean = (status) => {
    if (status === null || status === undefined) return null

    const normalizedStatus = normalizeConsentStatus(status)
    if (!normalizedStatus) return null

    if (normalizedStatus === CONSENT_STATUS_APPROVED.toUpperCase()) return true
    if (
        normalizedStatus === CONSENT_STATUS_REJECTED.toUpperCase() ||
        normalizedStatus === CONSENT_STATUS_REJECTED_COMPAT.toUpperCase()
    ) return false

    return null
}

const shouldPersistConsentForPerson = (repreguntarACliente, consentStatus) => {
    if (isRevokedConsentStatus(consentStatus)) {
        return false
    }

    return repreguntarACliente !== false
}

const resolveConsentDisplayMode = (consentStatus, repreguntarACliente) => {
    if (isRevokedConsentStatus(consentStatus)) {
        return CONSENT_DISPLAY_MODE_REVOKED
    }

    if (repreguntarACliente === true) {
        return CONSENT_DISPLAY_MODE_INTERACTIVE
    }

    const consentStatusAsBoolean = mapConsentStatusToBoolean(consentStatus)

    if (repreguntarACliente === false) {
        return consentStatusAsBoolean === true
            ? CONSENT_DISPLAY_MODE_APPROVED
            : CONSENT_DISPLAY_MODE_REGISTERED
    }

    return consentStatusAsBoolean === true
        ? CONSENT_DISPLAY_MODE_APPROVED
        : CONSENT_DISPLAY_MODE_INTERACTIVE
}

const getConsentInfoText = (displayMode) => {
    if (displayMode === CONSENT_DISPLAY_MODE_REVOKED) {
        return CONSENT_REVOKED_INFO_TEXT
    }

    if (displayMode === CONSENT_DISPLAY_MODE_APPROVED) {
        return CONSENT_ALREADY_APPROVED_TEXT
    }

    return CONSENT_ALREADY_REGISTERED_TEXT
}

const toLegacyConsentFlag = (value) => {
    return value ? 1 : 2
}

const resolveLegacyConsentFlag = (persistConsent, checkboxValue, consentStatus, currentFlag, fallbackFlag = null) => {
    if (persistConsent) {
        return toLegacyConsentFlag(checkboxValue)
    }

    if (isRevokedConsentStatus(consentStatus)) {
        if (fallbackFlag !== null && fallbackFlag !== undefined) {
            return fallbackFlag
        }

        return LEGACY_CONSENT_DEFAULT_FLAG
    }

    const consentStatusAsBoolean = mapConsentStatusToBoolean(consentStatus)
    if (consentStatusAsBoolean !== null) {
        return toLegacyConsentFlag(consentStatusAsBoolean)
    }

    if (currentFlag !== null && currentFlag !== undefined) {
        return currentFlag
    }

    return fallbackFlag
}

const Lpdp = ({ history }) => {
    const { state: generalState, dispatch: generalDispatch } = useContext(GeneralContext)
    let user = generalState.users.filter(user => user.isAuthenticated)[0]
    let prospecto = user.listaProspectos.filter(prospecto => prospecto.isSelected)[0]
    let cotizacion = prospecto.cotizaciones.filter(cotizacion => cotizacion.isSelected || cotizacion.completado)[0]

    const initialState = {
        estaCargando: true,
        errorCargando: null,
        valorModal: false,
        valorModalAdicional: false,
        codigoEstado: null,
        esAseguradoAdicional: false,
        idAcuerdoCliente: null,
        idAcuerdoClienteAdicional: null,
        documentoConsentimiento: null,
        estadoConsentimientoPrincipal: null,
        estadoConsentimientoAdicional: null,
        repreguntarConsentimientoPrincipal: null,
        repreguntarConsentimientoAdicional: null,
        modoConsentimientoPrincipal: CONSENT_DISPLAY_MODE_INTERACTIVE,
        modoConsentimientoAdicional: CONSENT_DISPLAY_MODE_INTERACTIVE
    }

    const [state, setState] = useState(initialState)

    const handleChangeConsentimientoPrincipal = (event) => {
        setState((state) => {
            return {
                ...state,
                valorModal: event.target.checked
            }
        })
    }

    const handleChangeConsentimientoAdicional = (event) => {
        setState((state) => {
            return {
                ...state,
                valorModalAdicional: event.target.checked
            }
        })
    }

    const putSolicitudes = async (flagPrincipal, flagAdicional) => {
        if (flagPrincipal === null && flagAdicional === null) {
            return true
        }

        const response = await actualizarSolicitud(
            cotizacion.idSolicitudMultiple,
            flagPrincipal,
            flagAdicional,
            null)
        if(response.error === null) {
            return true
        } else {
            return false
        }
    }

    const guardarConsentimientos = async () => {
        let success = true
        const tieneAseguradoAdicional = state.esAseguradoAdicional && cotizacion.aseguradoAdicional
        const enviarConsentimientoPrincipal = shouldPersistConsentForPerson(
            state.repreguntarConsentimientoPrincipal,
            state.estadoConsentimientoPrincipal
        )
        const enviarConsentimientoAdicional = tieneAseguradoAdicional &&
            shouldPersistConsentForPerson(
                state.repreguntarConsentimientoAdicional,
                state.estadoConsentimientoAdicional
            )

        if (!enviarConsentimientoPrincipal && !enviarConsentimientoAdicional) {
            return true
        }

        const documentoId = state.documentoConsentimiento?.directorioDocumentoId
        const versionId = state.documentoConsentimiento?.versionDocumentoId
        const idIntermediario = user?.info?.CodigoIntermediario

        if (!documentoId || !versionId || idIntermediario === null || idIntermediario === undefined || String(idIntermediario).trim() === '') {
            return false
        }

        const documentoDirectorioVersion = {
            documentoId,
            versionId
        }

        if (enviarConsentimientoPrincipal) {
            const estadoConsentimientoPrincipal = mapBooleanToConsentStatus(state.valorModal)

            // 1. Primario
            const datosPrimary = {
                tipoDocumento: prospecto.tipoDocumento,
                numeroDocumento: prospecto.numeroDocumento,
                estado: estadoConsentimientoPrincipal,
                idSolicitudMultiple: cotizacion.idSolicitudMultiple,
                codigoProducto: cotizacion.productos[0].codigoProducto,
                documentoDirectorioVersion
            }

            if (state.idAcuerdoCliente) {
                const res = await actualizarConsentimiento(state.idAcuerdoCliente, {
                    acuerdoClienteEstado: datosPrimary.estado,
                    idSolicitudMultiple: datosPrimary.idSolicitudMultiple,
                    directorioDocumento: {
                        directorioDocumentoId: datosPrimary.documentoDirectorioVersion.documentoId,
                        versionDocumentoId: datosPrimary.documentoDirectorioVersion.versionId
                    }
                }, idIntermediario)
                if (res.error) success = false
            } else {
                const res = await registrarConsentimiento(datosPrimary, idIntermediario)
                if (res.error) success = false
            }
        }

        // 2. Adicional
        if (enviarConsentimientoAdicional) {
            const estadoConsentimientoAdicional = mapBooleanToConsentStatus(state.valorModalAdicional)
            const datosAdd = {
                tipoDocumento: cotizacion.aseguradoAdicional.tipoDocumento,
                numeroDocumento: cotizacion.aseguradoAdicional.numeroDocumento,
                estado: estadoConsentimientoAdicional,
                idSolicitudMultiple: cotizacion.idSolicitudMultiple,
                codigoProducto: cotizacion.productos[0].codigoProducto,
                documentoDirectorioVersion
            }

            if (state.idAcuerdoClienteAdicional) {
                const res = await actualizarConsentimiento(state.idAcuerdoClienteAdicional, {
                    acuerdoClienteEstado: datosAdd.estado,
                    idSolicitudMultiple: datosAdd.idSolicitudMultiple,
                    directorioDocumento: {
                        directorioDocumentoId: datosAdd.documentoDirectorioVersion.documentoId,
                        versionDocumentoId: datosAdd.documentoDirectorioVersion.versionId
                    }
                }, idIntermediario)
                if (res.error) success = false
            } else {
                const res = await registrarConsentimiento(datosAdd, idIntermediario)
                if (res.error) success = false
            }
        }

        return success
    }

    const validarPantalla = async() => {
        generalDispatch(GeneralActions.updateEstadoCargando(true))
        try {
            // 1. Guardar en nuevos servicios de consentimiento
            const resCons = await guardarConsentimientos()
            if (!resCons) {
                return false
            }

            // 2. Guardar en servicio tradicional (Sync legacy state)
            const tieneAseguradoAdicional = state.esAseguradoAdicional && cotizacion.aseguradoAdicional
            const enviarConsentimientoPrincipal = shouldPersistConsentForPerson(
                state.repreguntarConsentimientoPrincipal,
                state.estadoConsentimientoPrincipal
            )
            const enviarConsentimientoAdicional = tieneAseguradoAdicional &&
                shouldPersistConsentForPerson(
                    state.repreguntarConsentimientoAdicional,
                    state.estadoConsentimientoAdicional
                )

            const flagPrincipal = resolveLegacyConsentFlag(
                enviarConsentimientoPrincipal,
                state.valorModal,
                state.estadoConsentimientoPrincipal,
                cotizacion.lpdp,
                LEGACY_CONSENT_DEFAULT_FLAG
            )
            const flagAdicional = resolveLegacyConsentFlag(
                enviarConsentimientoAdicional,
                state.valorModalAdicional,
                state.estadoConsentimientoAdicional,
                cotizacion.lpdpAdicional,
                tieneAseguradoAdicional ? LEGACY_CONSENT_DEFAULT_FLAG : null
            )

            generalDispatch(
                GeneralActions.updateLpdpProspecto(
                    flagPrincipal,
                    flagAdicional,
                    state.codigoEstado
                )
            )

            await putSolicitudes(flagPrincipal, flagAdicional).catch(() => {})

            return true
        } finally {
            generalDispatch(GeneralActions.updateEstadoCargando(false))
        }
    }

    const obtenerCodigoEstado = async () => {
        const response = await obtenerEstadoSolicitudMultiple(cotizacion.idSolicitudMultiple)
        if(response.error === null) {
            return response.estado
        } else {
            generalDispatch(GeneralActions.updateError(response.error, '¡Alerta!', true))
            return null
        }
    }

    const checkEsAseguradoAdicional = () => {
        let esAseguradoAdicional = false
        cotizacion.solicitudes.forEach(solicitud => {
            if (solicitud.indicadorAseguradoAdicional) {
                esAseguradoAdicional = true
            }
        })
        return esAseguradoAdicional
    }

    const cargarDatosIniciales = async () => {
        generalDispatch(GeneralActions.updateEstadoCargando(true))
        
        // 1. Obtener documentos (Texto dinámico)
        const resDoc = await obtenerDocumentosConsentimiento()
        let docData = null
        if(resDoc.error === null) {
            docData = resDoc.documento
        }

        // 2. Obtener estado de la solicitud multiple
        const estadoSolicitud = await obtenerCodigoEstado()

        // 3. Verificar si es asegurado adicional
        const esAseguradoAdicional = checkEsAseguradoAdicional()

        // 4. Consultar consentimientos existentes
        let idAcuerdo = null
        let idAcuerdoAdicional = null
        let valorModal = false
        let valorModalAdicional = false
        let estadoConsentimientoPrincipal = null
        let estadoConsentimientoAdicional = null
        let repreguntarConsentimientoPrincipal = null
        let repreguntarConsentimientoAdicional = null
        let modoConsentimientoPrincipal = CONSENT_DISPLAY_MODE_INTERACTIVE
        let modoConsentimientoAdicional = CONSENT_DISPLAY_MODE_INTERACTIVE

        const resCons = await consultarConsentimiento({
            tipoDocumento: prospecto.tipoDocumento,
            numeroDocumento: prospecto.numeroDocumento
        })

        if(resCons.error === null && resCons.datos) {
            idAcuerdo = resCons.datos.idAcuerdoCliente
            estadoConsentimientoPrincipal = resCons.datos.estado
            repreguntarConsentimientoPrincipal = resCons.datos.repreguntarACliente
            modoConsentimientoPrincipal = resolveConsentDisplayMode(
                estadoConsentimientoPrincipal,
                repreguntarConsentimientoPrincipal
            )

            if (modoConsentimientoPrincipal === CONSENT_DISPLAY_MODE_APPROVED) {
                valorModal = true
            }
        }

        if (esAseguradoAdicional && cotizacion.aseguradoAdicional) {
            const resConsAdd = await consultarConsentimiento({
                tipoDocumento: cotizacion.aseguradoAdicional.tipoDocumento,
                numeroDocumento: cotizacion.aseguradoAdicional.numeroDocumento
            })
            if(resConsAdd.error === null && resConsAdd.datos) {
                idAcuerdoAdicional = resConsAdd.datos.idAcuerdoCliente
                estadoConsentimientoAdicional = resConsAdd.datos.estado
                repreguntarConsentimientoAdicional = resConsAdd.datos.repreguntarACliente
                modoConsentimientoAdicional = resolveConsentDisplayMode(
                    estadoConsentimientoAdicional,
                    repreguntarConsentimientoAdicional
                )

                if (modoConsentimientoAdicional === CONSENT_DISPLAY_MODE_APPROVED) {
                    valorModalAdicional = true
                }
            }
        }

        setState((state) => {
            return {
                ...state,
                documentoConsentimiento: docData,
                idAcuerdoCliente: idAcuerdo,
                idAcuerdoClienteAdicional: idAcuerdoAdicional,
                valorModal,
                valorModalAdicional,
                esAseguradoAdicional: esAseguradoAdicional,
                estadoConsentimientoPrincipal,
                estadoConsentimientoAdicional,
                repreguntarConsentimientoPrincipal,
                repreguntarConsentimientoAdicional,
                modoConsentimientoPrincipal,
                modoConsentimientoAdicional,
                codigoEstado: estadoSolicitud,
                estaCargando: false
            }
        })

        generalDispatch(GeneralActions.updateEstadoCargando(false))
    }

    useEffect(() => {
        cargarDatosIniciales()
    }, [])

    //Modo lectura
    const deshabilitar = () => {
        return cotizacion.edicionInhabilitada === true
    }

    const [mover, setMover] = useState(false)
    const mueveContenido = (valor) => {
        setMover(valor)
    }

    const consentimientoDocumentoUrl = state.documentoConsentimiento?.url

    const isInteractiveConsentMode = (displayMode) => {
        return displayMode === CONSENT_DISPLAY_MODE_INTERACTIVE
    }

    const renderConsentInfo = (displayMode) => {
        const isRevokedMode = displayMode === CONSENT_DISPLAY_MODE_REVOKED
        const className = `${styles['consent-hidden-message']} ${
            isRevokedMode ? styles['consent-hidden-message--revoked'] : ''
        }`.trim()

        return (
            <p className={className}>
                {getConsentInfoText(displayMode)}
            </p>
        )
    }

    const renderConsentCheckbox = (options) => {
        const { name, checked, onChange } = options
        const isDisabled = deshabilitar()
        const checkboxId = `lpdp-${name}`

        return (
            <div className={`${styles['consent-card-wrapper']} ${isDisabled ? styles['consent-card--disabled'] : ''}`}>
                <input
                    id={checkboxId}
                    type="checkbox"
                    name={name}
                    checked={checked}
                    onChange={onChange}
                    disabled={isDisabled}
                />
                <label
                    htmlFor={checkboxId}
                    className={styles['consent-card']}
                >
                    <span className={styles['consent-card-text']}>
                        Doy mi{' '}
                        {
                            consentimientoDocumentoUrl
                                ?
                                <a
                                    href={consentimientoDocumentoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles['consent-card-link']}
                                >
                                    consentimiento para usos adicionales
                                </a>
                                : 'consentimiento para usos adicionales'
                        }
                        {' '}y disfrutar de los beneficios, promociones y descuentos creados para mi.
                    </span>
                </label>
            </div>
        )
    }

    const handleContinuarDirecto = () => {
        const tieneAseguradoAdicional = state.esAseguradoAdicional && cotizacion.aseguradoAdicional
        const enviarConsentimientoPrincipal = shouldPersistConsentForPerson(
            state.repreguntarConsentimientoPrincipal,
            state.estadoConsentimientoPrincipal
        )
        const enviarConsentimientoAdicional = tieneAseguradoAdicional &&
            shouldPersistConsentForPerson(
                state.repreguntarConsentimientoAdicional,
                state.estadoConsentimientoAdicional
            )

        const flagPrincipal = resolveLegacyConsentFlag(
            enviarConsentimientoPrincipal,
            state.valorModal,
            state.estadoConsentimientoPrincipal,
            cotizacion.lpdp,
            LEGACY_CONSENT_DEFAULT_FLAG
        )
        const flagAdicional = resolveLegacyConsentFlag(
            enviarConsentimientoAdicional,
            state.valorModalAdicional,
            state.estadoConsentimientoAdicional,
            cotizacion.lpdpAdicional,
            tieneAseguradoAdicional ? LEGACY_CONSENT_DEFAULT_FLAG : null
        )

        generalDispatch(
            GeneralActions.updateLpdpProspecto(
                flagPrincipal,
                flagAdicional,
                state.codigoEstado
            )
        )
        generalDispatch(GeneralActions.continuarProceso(history.location.pathname))
        history.push('/informacion/beneficiarios')
    }

    return (
        <div>
            <Header
                history={history}
            />
            <SubMenu history={history} modal={state.estaCargando} desplaza={mueveContenido} />
            <section className={styles['areadatosusuario']}
                style={mover ? {width: '93%', paddingLeft: '6.5%', height: '100vh'} : {}}
            >
                <div className={styles['areadatosprospecto']}>
                    <Subtitle
                        sname="Ley de Protección de Datos Personales"
                    />
                    <div className={styles['arealeydatos']}>
                        <h3>Cláusulas de usos adicionales y transferencia de información</h3>
                        {
                            state.esAseguradoAdicional
                                ?
                                <div className={styles['consent-group']}>
                                    <div className={styles['consent-person']}>
                                        <p className={styles['consent-person-title']}><b>Asegurado principal</b></p>
                                        {
                                            isInteractiveConsentMode(state.modoConsentimientoPrincipal)
                                                ? renderConsentCheckbox({
                                                    name: 'consentimientoPrincipal',
                                                    checked: state.valorModal,
                                                    onChange: handleChangeConsentimientoPrincipal
                                                })
                                                : renderConsentInfo(state.modoConsentimientoPrincipal)
                                        }
                                    </div>
                                    <div className={styles['consent-person']}>
                                        <p className={styles['consent-person-title']}><b>Asegurado adicional</b></p>
                                        {
                                            isInteractiveConsentMode(state.modoConsentimientoAdicional)
                                                ? renderConsentCheckbox({
                                                    name: 'consentimientoAdicional',
                                                    checked: state.valorModalAdicional,
                                                    onChange: handleChangeConsentimientoAdicional
                                                })
                                                : renderConsentInfo(state.modoConsentimientoAdicional)
                                        }
                                    </div>
                                </div>
                                :
                                <div>
                                    {
                                        isInteractiveConsentMode(state.modoConsentimientoPrincipal)
                                            ? renderConsentCheckbox({
                                                name: 'consentimientoPrincipal',
                                                checked: state.valorModal,
                                                onChange: handleChangeConsentimientoPrincipal
                                            })
                                            : renderConsentInfo(state.modoConsentimientoPrincipal)
                                    }
                                </div>
                        }
                    </div>
                </div>
                <div className={styles['direct-continue-container']}>
                    <button
                        type="button"
                        className={styles['direct-continue-button']}
                        onClick={handleContinuarDirecto}
                    >
                        Avanzar directo al siguiente paso
                    </button>
                </div>
                <Footer history={history} validarPantalla={validarPantalla} />
            </section>
        </div>
    )
}

export default Lpdp